import React, { ReactNode, useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import LoginPage from './LoginPage';
import styled from 'styled-components';
import { AUTH_SESSION_EXPIRED_EVENT, isSessionExpired } from '../authSession';
import { DEV_MODE } from '../authConfig';
import { useDevAuth } from './DevAuthProvider';
import SkeletonBase, { SkeletonText } from './Skeleton/SkeletonLoader';

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  font-family: Arial, sans-serif;
`;

const LoadingCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 40px;
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #2c3e50;
  font-size: 16px;
  margin: 0;
`;

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const devAuth = DEV_MODE ? useDevAuth() : null;
  const [sessionExpired, setSessionExpired] = useState(isSessionExpired);

  useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // In development mode, use dev authentication
  if (DEV_MODE && devAuth) {
    if (!devAuth.isAuthenticated) {
      return <LoginPage />;
    }
    return <>{children}</>;
  }

  if (sessionExpired) {
    return <LoginPage sessionExpired />;
  }

  // Show loading while authentication is in progress
  if (inProgress !== 'none') {
    return (
      <LoadingContainer>
        <LoadingCard>
          <SkeletonBase $width="40px" $height="40px" $borderRadius="50%" style={{ margin: '0 auto 20px' }} />
          <SkeletonText lines={1} width="150px" />
        </LoadingCard>
      </LoadingContainer>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show the protected content (LACE Tool form) if authenticated
  return <>{children}</>;
};

export default ProtectedRoute;
